async function getLLMResponse(messages, prompt, phase, setResponseCallback = null) {
  const phaseConfigs = {
    "clarifying-questions": { model: 'gpt-4-turbo', temperature: 0.35, top_p: 0.7, presence_penalty: 0.4, frequency_penalty: 0.2, max_tokens: 450, stream: false },   
    "big-idea": { model: 'gpt-4-turbo', temperature: 0.85, top_p: 0.95, presence_penalty: 0.3, frequency_penalty: 0.2, max_tokens: 1800, stream: true },
    "refine-analysis": { model: 'gpt-4-turbo', temperature: 0.6, top_p: 0.8, presence_penalty: 0.3, frequency_penalty: 0.2, max_tokens: 2000, stream: true },
    "sharpen-analysis": { model: 'gpt-4-turbo', temperature: 0.6, top_p: 0.8, presence_penalty: 0.3, frequency_penalty: 0.2, max_tokens: 2000, stream: true },
    "generate-questions": { model: 'gpt-4-turbo', temperature: 0.3, top_p: 0.7, presence_penalty: 0.4, frequency_penalty: 0.2, max_tokens: 400, stream: false },
    "segment-question": { model: 'gpt-4-turbo', temperature: 0.4, top_p: 0.75, presence_penalty: 0.5, frequency_penalty: 0.3, max_tokens: 200, stream: false },
    "deep-analysis": { model: 'gpt-4-turbo', temperature: 0.45, top_p: 0.75, presence_penalty: 0.5, frequency_penalty: 0.4, max_tokens: 1800, stream: true },
    "validation-strategy": { model: 'gpt-4-turbo', temperature: 0.5, top_p: 0.8, presence_penalty: 0.4, frequency_penalty: 0.3, max_tokens: 1800, stream: true },
    "elevator-pitch": { model: 'gpt-4-turbo', temperature: 0.75, top_p: 0.9, presence_penalty: 0.3, frequency_penalty: 0.2, max_tokens: 100, stream: false },
    "executive-summary": { model: 'gpt-4-turbo', temperature: 0.45, top_p: 0.75, presence_penalty: 0.4, frequency_penalty: 0.3, max_tokens: 1500, stream: false },
    default: { model: 'gpt-4-turbo', temperature: 0.6, top_p: 0.85, presence_penalty: 0.3, frequency_penalty: 0.2, max_tokens: 900, stream: false }
  };

  const config = phaseConfigs[phase] || phaseConfigs.default;
  const updatedMessages = [...messages, { role: "user", content: prompt }];
  console.log(`Calling OpenAI API with streaming: ${config.stream} for phase: ${phase}`);

  try {
    if (config.stream && setResponseCallback) {
      // ✅ Streaming Response Handling
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: config.model,
          messages: updatedMessages,
          max_tokens: config.max_tokens,
          temperature: config.temperature,
          top_p: config.top_p,
          presence_penalty: config.presence_penalty,
          frequency_penalty: config.frequency_penalty,
          stream: true
        })
      });

      if (!response.body) throw new Error("No response body received from OpenAI.");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // 🔥 Fix: Split chunk into lines and process only valid JSON
        const lines = chunk.split("\n").map(line => line.trim()).filter(line => line.startsWith("data: "));

        for (const line of lines) {
          try {
            const jsonString = line.slice(6); // Remove "data: " prefix
            if (jsonString === "[DONE]") continue; // ✅ Ignore "DONE" chunk

            const json = JSON.parse(jsonString); // ✅ Parse only valid JSON
            const content = json.choices?.[0]?.delta?.content || "";

            if (content) {
              finalResponse += content;
              setResponseCallback((prev) => prev + content); // Stream live updates
            }
          } catch (err) {
            console.warn("Skipping non-JSON chunk:", line); // Debugging log
          }
        }
      }

      return finalResponse;
    } else {
      // ✅ Non-Streaming Response Handling
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: config.model,
          messages: updatedMessages,
          max_tokens: config.max_tokens,
          temperature: config.temperature,
          top_p: config.top_p,
          presence_penalty: config.presence_penalty,
          frequency_penalty: config.frequency_penalty,
          stream: false // Explicitly disable streaming
        })
      });

      const responseData = await response.json();
      return responseData.choices[0].message.content;
    }
  } catch (error) {
    console.error("🔥 Error in getLLMResponse:", error);
    throw error;
  }
}

export default getLLMResponse;
